import React, { useContext, useMemo } from "react";
import Avatar from "../../../Components/Avatar/Avatar";
import "./UsersList.scss";
import { useTranslation } from "react-i18next";
import UserActions from "./UserActions/UserActions";
import { userActions, type UserSubmitHandler } from "./UsersListUtils";
import {
  createColumnHelper,
  type CellContext,
  type FilterFn,
  type SortingFn,
} from "@tanstack/react-table";
import ChangeUserPassword from "../../../CustomerProvisioning/ChangeUserPassword/ChangeUserPassword";
import {
  getFormattedDate,
  Tooltip,
  TextButton,
  IconButton,
  Table,
  TableCell,
  tableCellTypesEnum,
  tableFilterTypes,
  ModalContext,
  showDialog,
  MultipleValuesDetailsItemPopover,
  SkeletonLoader,
} from "@SeemplicityDev/shared";
import useRoles from "../useRoles";

export const identityProviders = {
  SAML: "SAML",
  COGNITO: "COGNITO",
} as const;

type IdentityProvider =
  (typeof identityProviders)[keyof typeof identityProviders];

const SAML_USER_MANAGEMENT_WARNING =
  "SAML user access should be managed in the Identity provider";

export interface UserRole {
  id?: string;
  name?: string;
}

export interface UserProfile {
  id?: string;
  name?: string;
  hasScopes?: number | boolean;
  readOnly?: boolean;
}

export interface UsersListUser {
  email?: string;
  firstName?: string;
  lastName?: string;
  login?: string | null;
  disabled?: boolean;
  id?: string;
  role?: UserRole | null;
  profiles?: UserProfile[];
  userIdentityProvider?: IdentityProvider | string | null;
}

const multiSelectFilterFnByName: FilterFn<UsersListUser> = (
  row,
  columnId,
  filterValue
) => {
  const rowValue = row.getValue<UserRole | null>(columnId);
  return filterValue.includes(rowValue?.name) || filterValue.includes("");
};

interface UsersListProps {
  users: UsersListUser[];
  loading: boolean;
  profilesLoading?: boolean;
  profilesSuggestions?: UserProfile[];
  allowedDomains?: string[];
  isCNC: boolean;
  onAdd: UserSubmitHandler;
  onEdit: UserSubmitHandler;
  onChangePassword?: (newPass: string, userId: string, name: string) => void;
  onReInvite: (userId: string, email: string) => void;
  onDelete: (userId: string, email: string, name: string) => void;
  ssoEnforced?: boolean;
}

const UsersList: React.FC<UsersListProps> = ({
  users,
  loading,
  profilesLoading,
  profilesSuggestions,
  allowedDomains,
  isCNC,
  onAdd,
  onEdit,
  onChangePassword,
  onReInvite,
  onDelete,
  ssoEnforced,
}) => {
  const { openModal } = useContext(ModalContext);
  const { t } = useTranslation();

  const { roles } = useRoles();

  const sortByTime: SortingFn<UsersListUser> = (rowA, rowB, columnId) => {
    const dateA = new Date(rowA.getValue<string>(columnId));
    const dateB = new Date(rowB.getValue<string>(columnId));

    if (dateA < dateB) {
      return 1;
    } else if (dateA > dateB) {
      return -1;
    }
    return 0;
  };

  const columnHelper = createColumnHelper<UsersListUser>();

  const generateActionsCell = (
    props: CellContext<UsersListUser, unknown>
  ): React.ReactElement => {
    const email = props.row.getValue<string>("email");
    const firstName = props.row.original.firstName;
    const lastName = props.row.original.lastName;
    // `id` is optional on `UsersListUser` but is always present for a rendered
    // user row; coalesce to satisfy the non-optional handler signatures.
    const userId = props.row.original.id ?? "";
    const name = props.row.getValue<string>("name");
    const role = props.row.getValue<UserRole>("role");
    const profiles = props.row.getValue<UserProfile[]>("profiles");
    const isSamlUser =
      props.row.original.userIdentityProvider === identityProviders.SAML;
    const hasLogged = props.row.getValue<string | null>("login");
    const resetPasswordTip = hasLogged
      ? "Reset Password"
      : "Resend Invitation Email";

    return (
      <TableCell className={"actions-cell"}>
        <div className={"users-list-actions users-list-cell form-button-group"}>
          {isCNC && (
            <TextButton
              wrapperClassName={"change-pass-btn"}
              data-testid={`${userId}-change-pass`}
              label={"Change Password"}
              onClick={() => {
                openModal({
                  title: "Change Password",
                  size: "xs",
                  overlay: true,
                  component: (
                    <ChangeUserPassword
                      OnSubmit={async (newPass: string) => {
                        onChangePassword?.(newPass, userId, name);
                      }}
                    />
                  ),
                });
              }}
            />
          )}

          <IconButton
            data-testid={`edit-user-${userId}`}
            disabled={!hasLogged}
            isIcon="seem-icon-edit"
            isRound
            type={`button`}
            withTooltip={{
              content: !props.row.original?.login ? "" : "Edit User",
            }}
            onClick={() => {
              // we have stop propagation on table row (<Tr />) due to other places where it is necessary
              // until we think of a wider solution, clicking on the body program atically (in this case - to close the modal of the table header)
              document.body.click();
              openModal({
                title: "Edit User",
                className: "add-edit-user-modal",
                size: "s",
                overlay: true,
                component: (
                  <UserActions
                    mode={userActions.EDIT}
                    data={{
                      email,
                      firstName,
                      lastName,
                      id: userId,
                      role,
                      profiles,
                    }}
                    profilesSuggestions={profilesSuggestions}
                    onSubmit={onEdit}
                    isCNC={isCNC}
                    isSamlUser={isSamlUser}
                  />
                ),
              });
            }}
          />

          <IconButton
            data-testid={`resend-user-${userId}`}
            isIcon="seem-icon-refresh"
            disabled={isSamlUser}
            type={`button`}
            onClick={() => onReInvite(userId, email)}
            withTooltip={{
              content: isSamlUser
                ? SAML_USER_MANAGEMENT_WARNING
                : resetPasswordTip,
            }}
          />

          <IconButton
            data-testid={`delete-user-${userId}`}
            disabled={isSamlUser}
            isIcon="seem-icon-bin"
            type={`button`}
            withTooltip={{
              content: isSamlUser
                ? SAML_USER_MANAGEMENT_WARNING
                : "Delete User",
            }}
            onClick={() =>
              showDialog({
                title: "Delete User",
                message: (
                  <div className={"modal-text"}>
                    Delete the User
                    <span className={"modal-live-text"}> {name}</span>?
                  </div>
                ),
                buttons: [
                  {
                    "data-testid": "cancel-delete",
                    label: "Cancel",
                  },
                  {
                    "data-testid": "confirm-delete",
                    buttonStyle: "danger",
                    label: "Delete User",
                    type: "submit",
                    onClick: () => onDelete(userId, email, `${name}`),
                  },
                ],
              })
            }
          />
        </div>
      </TableCell>
    );
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => `${row?.firstName} ${row?.lastName}`, {
        id: "name",
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div className={"users-list-name users-list-cell"}>
              <Avatar
                name={props.getValue()}
                disabled={!props.row.original?.login}
                size={"s"}
              />
              <Tooltip content={props.getValue()}>
                <span className={"user-text"}>{props.getValue()}</span>
              </Tooltip>
            </div>
          </TableCell>
        ),
        header: "Name",

        sortingFn: "alphanumericCaseSensitive",
        size: 200,
        filterFn: tableFilterTypes.TEXT.filterFn,
        meta: {
          filterType: tableFilterTypes.TEXT.filterType,
        },
      }),
      columnHelper.accessor("email", {
        cell: (info) => (
          <TableCell>
            <Tooltip content={info.getValue()}>
              <span>{info.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        header: "Email",
        minSize: 140,
        filterFn: tableFilterTypes.TEXT.filterFn,
        meta: {
          filterType: tableFilterTypes.TEXT.filterType,
        },
      }),
      columnHelper.accessor("role", {
        cell: (props) => {
          return (
            <TableCell>
              <Tooltip content={props.getValue()?.name}>
                <span>{props.getValue()?.name}</span>
              </Tooltip>
            </TableCell>
          );
        },
        enableSorting: false,
        header: "Role",

        size: 130,
        filterFn: multiSelectFilterFnByName,
        meta: {
          filterType: tableFilterTypes.MULTI_SELECT.filterType,
          filterOptions: roles?.map((option) => option.label),
        },
      }),
      columnHelper.accessor("profiles", {
        cell: (props) => {
          if (profilesLoading) {
            return (
              <TableCell type={tableCellTypesEnum.COMPONENT}>
                <SkeletonLoader
                  height={"19px"}
                  width={"100%"}
                  data-testid={"user-profiles-loader"}
                />
              </TableCell>
            );
          }
          const profileNames = (props.getValue() ?? []).flatMap(({ name }) =>
            name ? [name] : []
          );
          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              {!!profileNames.length && (
                <MultipleValuesDetailsItemPopover
                  values={profileNames}
                  className={"user-profiles-details"}
                />
              )}
            </TableCell>
          );
        },
        size: 210,
        minSize: 130,
        enableSorting: false,
        header: "Profiles",
        enableColumnFilter: false,
      }),
      columnHelper.accessor("login", {
        header: "Last Login",
        size: 160,
        enableColumnFilter: false,
        sortingFn: sortByTime,
        cell: (info) =>
          info.getValue() ? (
            <TableCell>
              <Tooltip content={getFormattedDate(info.getValue()!)}>
                <span>{getFormattedDate(info.getValue()!)}</span>
              </Tooltip>
            </TableCell>
          ) : (
            <TableCell>---</TableCell>
          ),
      }),
      columnHelper.display({
        id: "actions",
        size: isCNC ? 260 : 130,
        cell: (props) => generateActionsCell(props),
      }),
    ];
  }, [columnHelper, roles, profilesLoading]);

  return (
    <div className="users-list-container">
      <TextButton
        buttonStyle={"primary"}
        wrapperClassName={"sidebar-display-area-top-button"}
        label={t("+ Add User")}
        className={"add-user-btn"}
        size={"large"}
        data-testid={"add-user"}
        onClick={() =>
          openModal({
            title: "Add User",
            size: "s",
            overlay: true,
            className: "add-edit-user-modal",
            component: (
              <UserActions
                isCNC={isCNC}
                mode={userActions.CREATE}
                allowedDomains={allowedDomains}
                profilesSuggestions={profilesSuggestions}
                onSubmit={onAdd}
              />
            ),
          })
        }
        title={ssoEnforced ? undefined : t("Add")}
        withTooltip={
          ssoEnforced ? { content: SAML_USER_MANAGEMENT_WARNING } : undefined
        }
        disabled={ssoEnforced}
      />
      <div className={`users-list-table ${isCNC ? "is-cnc" : ""}`}>
        <Table
          loaderRowsAmount={8}
          data-testid={"users"}
          noBorder
          loading={loading}
          columns={columns}
          data={users}
          initialSortBy={[{ id: "login" }]}
          initialFilters={[{ id: "role", value: [""] }]}
        />
      </div>
    </div>
  );
};

export default UsersList;
